import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { realtimeInsightsService } from '../../services/realtimeInsights/RealtimeInsightsService';

// Request interface for the API calls
export interface RealtimeInsightsRequest {
  businessUnits: string;
  fields: string;
  groups: string;
  statuses?: string;
}

// Response interface - adjust this based on actual API response structure
export interface RealtimeInsightsData {
  // Add actual response structure fields here
  [key: string]: string | number | boolean | null | undefined | object;
}
export interface FixedColumnData {
  order: number;
  label: string;
  key: string;
}
export interface ColumnData {
  order: number;
  label: string;
  key: string;
  selected: boolean;
}
function moveAlarmsFirst<T extends { key: string; order: number }>(cols: T[]): T[] {
  const index = cols.findIndex((c) => c.key === 'alarms');
  if (index <= 0) return cols; // either not found or already first

  const newCols = [...cols];
  const [alarmsCol] = newCols.splice(index, 1);
  newCols.unshift(alarmsCol);

  // reassign order to preserve correct sequence
  return newCols.map((col, i) => ({ ...col, order: i + 1 }));
}
export function getColumnObjects(obj: unknown, prefix = ''): ColumnData[] {
  let result: ColumnData[] = [];

  const labelMapSentenceCase: Record<string, string> = {
    alarms: 'Alarms',
    ControlMode: 'Control mode',
    'Freq (Hz)': 'Freq (hz)',
    'PDP(psig)': 'PDP (psig)',
    'Pump Size': 'Pump size',
    'Pump Intake Pressure': 'Pump intake pressure',
    'Flowing Pressure Channel': 'Flowing pressure channel',
    'Gas Inj. Rate': 'Gas inj. rate',
    'Tubing Temp': 'Tubing temp',
    'Tubing Temperature': 'Tubing temperature',
  };

  const formatLabel = (key: string): string => {
    return labelMapSentenceCase[key] || key;
  };

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const path = `${prefix}[${index}]`;
      if (item && typeof item === 'object') {
        result.push(...getColumnObjects(item, path));
      }
    });
  } else if (obj && typeof obj === 'object') {
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        result.push(...getColumnObjects(value, fullPath));
      } else {
        const currKey = {
          order: result.length + 1,
          label: formatLabel(key),
          key: fullPath,
          selected: true,
        };
        result.push(currKey);
      }
    }
  }
  result = moveAlarmsFirst(result);

  return result;
}

export interface ESPItem {
  wellDetails: {
    'Freq (Hz)': string;
    'PIP (psig)': string;
    'Last S/D': string;
    'THP (psig)': string;
    'CHP (psig)': string;
    'PDP(psig)': string;
    'Vib X (g)': string;
    'Vib Y (g)': string;
    ControlMode: string;
  };
  wellName: string;
  status: string;
  alarms: number;
}

// State interface
export interface RealtimeInsightsState {
  espData: ESPItem[] | null;
  gasLiftData: ESPItem[] | null;
  rodLiftData: ESPItem[] | null;
  espLoading: boolean;
  gasLiftLoading: boolean;
  rodLiftLoading: boolean;
  espError: string | null;
  gasLiftError: string | null;
  rodLiftError: string | null;
  espColumns: ColumnData[];
  gasLiftColumns: ColumnData[];
  rodLiftColumns: ColumnData[];
}

const initialState: RealtimeInsightsState = {
  espData: null,
  gasLiftData: null,
  rodLiftData: null,
  espLoading: false,
  gasLiftLoading: false,
  rodLiftLoading: false,
  espError: null,
  gasLiftError: null,
  rodLiftError: null,
  espColumns: [],
  gasLiftColumns: [],
  rodLiftColumns: [],
};

// Async thunks for API calls
export const fetchESPRealtimeData = createAsyncThunk<ESPItem[], RealtimeInsightsRequest, { rejectValue: string }>(
  'realtimeInsights/fetchESPData',
  async (request, { rejectWithValue }) => {
    try {
      const response = await realtimeInsightsService.getESPData(request);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch ESP data');
    }
  },
);

export const fetchGasLiftRealtimeData = createAsyncThunk<ESPItem[], RealtimeInsightsRequest, { rejectValue: string }>(
  'realtimeInsights/fetchGasLiftData',
  async (request, { rejectWithValue }) => {
    try {
      const response = await realtimeInsightsService.getGasLiftData(request);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch Gas Lift data');
    }
  },
);

export const fetchRodLiftRealtimeData = createAsyncThunk<ESPItem[], RealtimeInsightsRequest, { rejectValue: string }>(
  'realtimeInsights/fetchRodLiftData',
  async (request, { rejectWithValue }) => {
    try {
      const response = await realtimeInsightsService.getRodLiftData(request);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch Rod Lift data');
    }
  },
);

// Slice
const realtimeInsightsSlice = createSlice({
  name: 'realtimeInsights',
  initialState,
  reducers: {
    clearESPData: (state) => {
      state.espData = null;
      state.espError = null;
    },
    clearGasLiftData: (state) => {
      state.gasLiftData = null;
      state.gasLiftError = null;
    },
    clearRodLiftData: (state) => {
      state.rodLiftData = null;
      state.rodLiftError = null;
    },
    clearAllData: (state) => {
      state.espData = null;
      state.gasLiftData = null;
      state.rodLiftData = null;
      state.espError = null;
      state.gasLiftError = null;
      state.rodLiftError = null;
    },
  },
  extraReducers: (builder) => {
    // ESP data
    builder
      .addCase(fetchESPRealtimeData.pending, (state) => {
        state.espLoading = true;
        state.espError = null;
      })
      .addCase(fetchESPRealtimeData.fulfilled, (state, action) => {
        state.espLoading = false;
        state.espData = action.payload;
        state.espColumns = action.payload?.[0] ? getColumnObjects(action.payload[0]) : [];
        state.espError = null;
      })
      .addCase(fetchESPRealtimeData.rejected, (state, action) => {
        state.espLoading = false;
        state.espData = null;
        state.espError = action.payload || 'Failed to fetch ESP data';
      });

    // Gas Lift data
    builder
      .addCase(fetchGasLiftRealtimeData.pending, (state) => {
        state.gasLiftLoading = true;
        state.gasLiftError = null;
      })
      .addCase(fetchGasLiftRealtimeData.fulfilled, (state, action) => {
        state.gasLiftLoading = false;
        state.gasLiftData = action.payload;
        state.gasLiftColumns = action.payload?.[0] ? getColumnObjects(action.payload[0]) : [];
        state.gasLiftError = null;
      })
      .addCase(fetchGasLiftRealtimeData.rejected, (state, action) => {
        state.gasLiftLoading = false;
        state.gasLiftData = null;
        state.gasLiftError = action.payload || 'Failed to fetch Gas Lift data';
      });

    // Rod Lift data
    builder
      .addCase(fetchRodLiftRealtimeData.pending, (state) => {
        state.rodLiftLoading = true;
        state.rodLiftError = null;
      })
      .addCase(fetchRodLiftRealtimeData.fulfilled, (state, action) => {
        state.rodLiftLoading = false;
        state.rodLiftData = action.payload;
        state.rodLiftColumns = action.payload?.[0] ? getColumnObjects(action.payload[0]) : [];
        state.rodLiftError = null;
      })
      .addCase(fetchRodLiftRealtimeData.rejected, (state, action) => {
        state.rodLiftLoading = false;
        state.rodLiftData = null;
        state.rodLiftError = action.payload || 'Failed to fetch Rod Lift data';
      });
  },
});

// Actions
export const { clearESPData, clearGasLiftData, clearRodLiftData, clearAllData } = realtimeInsightsSlice.actions;

// Selectors
export const selectESPData = (state: { realtimeInsights: RealtimeInsightsState }) => state.realtimeInsights.espData;
export const selectGasLiftData = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.gasLiftData;
export const selectRodLiftData = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.rodLiftData;
export const selectESPLoading = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.espLoading;
export const selectGasLiftLoading = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.gasLiftLoading;
export const selectRodLiftLoading = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.rodLiftLoading;
export const selectESPError = (state: { realtimeInsights: RealtimeInsightsState }) => state.realtimeInsights.espError;
export const selectGasLiftError = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.gasLiftError;
export const selectRodLiftError = (state: { realtimeInsights: RealtimeInsightsState }) =>
  state.realtimeInsights.rodLiftError;

export default realtimeInsightsSlice.reducer;
